import { useEffect, useMemo, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography
} from '@mui/material'
import { useAuth } from 'src/contexts/AuthContext'
import { doc } from 'firebase/firestore'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { firestore } from 'src/shims/firebase'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { Timestamp } from 'firebase/firestore'

type GroupSubscriberRow = {
  id?: string
  mail?: string
  firstname?: string
  lastname?: string
  company?: string | null
  active?: boolean
  inactiveAt?: Timestamp | { seconds: number }
  subscriptionProviderEndDate?: number
}

function tsToDate(ts: Timestamp | { seconds: number } | undefined | null): Date | null {
  if (!ts) return null
  if (typeof (ts as { seconds?: number }).seconds === 'number') {
    return new Date((ts as { seconds: number }).seconds * 1000)
  }
  return null
}

function formatUnixSeconds(sec: number | undefined | null): string {
  if (sec == null || sec <= 0) return '—'
  try {
    return format(new Date(sec * 1000), "d MMM yyyy à HH:mm", { locale: fr })
  } catch {
    return '—'
  }
}

export function TeamMemberDetailPage() {
  const { memberUid } = useParams<{ memberUid: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const ownerUid = user?.uid ?? ''

  const [groupData, groupLoading] = useDocumentData(ownerUid ? doc(firestore, 'subscription', ownerUid, 'groupSubscription', 'data') : null)

  const member = useMemo(() => {
    const rows = (groupData?.subscribers ?? []) as GroupSubscriberRow[]
    return rows.find((s) => s.id === memberUid) ?? null
  }, [groupData, memberUid])

  const stripeSubscriptionId = (groupData as { stripeSubscriptionId?: string })?.stripeSubscriptionId

  const [subscriptionDoc, subLoading] = useDocumentData(
    memberUid ? doc(firestore, 'subscription', memberUid) : null
  )

  const grantDocId =
    ownerUid && stripeSubscriptionId ? `${ownerUid}__${stripeSubscriptionId}` : ''

  const [grantDoc, grantLoading] = useDocumentData(
    memberUid && grantDocId
      ? doc(firestore, 'subscription', memberUid, 'distributorGrants', grantDocId)
      : null
  )

  useEffect(() => {
    if (!ownerUid || !memberUid) return
    if (groupLoading || !groupData) return
    if (!member) {
      navigate('/team', { replace: true })
    }
  }, [ownerUid, memberUid, groupLoading, groupData, member, navigate])

  const displayName = useMemo(() => {
    if (!member) return ''
    const n = [member.firstname, member.lastname].filter(Boolean).join(' ').trim()
    return n || member.mail || 'Membre'
  }, [member])

  const loading = groupLoading || subLoading || grantLoading

  if (!ownerUid || !memberUid) {
    return null
  }

  if (groupLoading && !groupData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!member) {
    return null
  }

  const sub = subscriptionDoc as Record<string, unknown> | undefined
  const grant = grantDoc as
    | {
        expiresAt?: number
        active?: boolean
        sponsorUid?: string
        stripeSubscriptionId?: string
      }
    | undefined

  const inactiveAtDate = tsToDate(member.inactiveAt as Timestamp | { seconds: number } | undefined)

  return (
    <>
      <Button
        component={Link}
        to="/team"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}>
        Retour à l'équipe
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Fiche membre — lecture seule (l'email sert de connexion ; pas de modification ici)
          </Typography>
        </Box>
        <Chip
          label={member.active === false ? 'Inactif' : 'Actif'}
          color={member.active === false ? 'default' : 'success'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5
        }}>
        <Box>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
            }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 2, letterSpacing: 0.5 }}>
                IDENTITÉ
              </Typography>
              <StackRow icon={<EmailOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Email">
                {member.mail ?? '—'}
              </StackRow>
              <StackRow icon={<PersonOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Prénom">
                {member.firstname ?? '—'}
              </StackRow>
              <StackRow icon={<BadgeOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Nom">
                {member.lastname ?? '—'}
              </StackRow>
              <StackRow icon={<BusinessOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Entreprise">
                {member.company ?? '—'}
              </StackRow>
              <Divider sx={{ my: 2 }} />
              <StackRow icon={<InfoOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Identifiant Firebase">
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {member.id ?? '—'}
                </Typography>
              </StackRow>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
            }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 2, letterSpacing: 0.5 }}>
                ACCÈS GROUPE & LICENCE
              </Typography>
              {member.active === false && inactiveAtDate && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Retiré du siège le {format(inactiveAtDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })}.
                </Typography>
              )}
              <StackRow
                icon={<EventAvailableOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
                label="Fin d'accès (siège groupe)">
                {member.subscriptionProviderEndDate
                  ? formatUnixSeconds(member.subscriptionProviderEndDate)
                  : grant?.expiresAt
                    ? formatUnixSeconds(grant.expiresAt)
                    : loading
                      ? '…'
                      : '—'}
              </StackRow>
              <StackRow icon={<InfoOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />} label="Grant actif">
                {grant?.active === false ? 'Non' : grant?.active === true ? 'Oui' : '—'}
              </StackRow>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Document subscription/{memberUid?.slice(0, 8)}…
              </Typography>
              <StackRow label="Fin accès distributeur (agrégé)">
                {formatUnixSeconds(sub?.expirationDateDistributor as number | undefined)}
              </StackRow>
              <StackRow label="Fin période Stripe (compte membre)">
                {formatUnixSeconds(sub?.expirationDateStripe as number | undefined)}
              </StackRow>
              <StackRow label="Abonnement personnel (Stripe)">
                {(sub?.isStripe as boolean) ? 'Oui' : 'Non'}
              </StackRow>
              <StackRow label="Licence distributeur active">
                {(sub?.isDistributor as boolean) ? 'Oui' : 'Non'}
              </StackRow>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 2.5,
          borderRadius: 2,
          backgroundColor: 'rgba(66,119,185,0.06)',
          border: '1px solid rgba(66,119,185,0.15)'
        }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
          Les dates proviennent de Firestore (synchro avec Stripe / webhooks). En cas d'écart, le portail Stripe ou un
          nouvel ajout de siège remettra les données à jour.
        </Typography>
      </Paper>
    </>
  )
}

function StackRow({
  icon,
  label,
  children
}: {
  icon?: ReactNode
  label: string
  children: ReactNode
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.75, alignItems: 'flex-start' }}>
      {icon ? <Box sx={{ pt: 0.25 }}>{icon}</Box> : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.25 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  )
}
